/**
 * OMNI · Section workspace (Section 9 · Complaints wired end-to-end).
 *
 * Renders every field of a section as a card. Each card shows:
 *   - Field ref (OMNI-9.1.a) · label · type
 *   - Current value (from lake or human override)
 *   - Provenance chip (Altara-native / Data connector / Manual / Not wired)
 *   - Source system, computed_at, confidence
 *   - Override toggle + reason capture
 *   - Suggest-mapping (LLM) CTA for unmapped fields
 *
 * Route: /regulatory/omni/:period_id/sections/:section_n
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatDistanceToNow } from "date-fns";
import {
  ChevronLeft, RefreshCw, Sparkles, Save, Pencil, X,
  CheckCircle2, AlertTriangle, Circle, Loader2, Database,
  User, Clock, ArrowRight, Link2, FileCheck2,
} from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

const SOURCE_CHIP = {
  native:    { label: "Altara native",   bg: "#D1FAE5", fg: "#065F46" },
  connector: { label: "Data connector",  bg: "#DBEAFE", fg: "#1E40AF" },
  manual:    { label: "Manual override", bg: "#FEF3C7", fg: "#92400E" },
  unsourced: { label: "Awaiting connector", bg: "#F1F5F9", fg: "#64748B" },
};

const STATUS_ICON = {
  ready:       { Icon: CheckCircle2, color: "#059669" },
  in_progress: { Icon: Loader2,      color: "#0369A1" },
  warning:     { Icon: AlertTriangle,color: "#D97706" },
  blocked:     { Icon: AlertTriangle,color: "#E11D48" },
  not_started: { Icon: Circle,       color: "#94A3B8" },
};

export default function OmniSectionPage() {
  const { period_id, section_n } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [suggesting, setSuggesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: sectionData }, entitiesRes, countryRes] = await Promise.all([
        api.get(`/regulatory/omni/periods/${period_id}/sections/${section_n}`),
        api.get("/regulatory/entities").catch(() => ({ data: { entities: [] } })),
        api.get("/regulatory/enumerations/VRBS_Country").catch(() => ({ data: { values: [] } })),
      ]);
      setPayload({
        ...sectionData,
        entities: entitiesRes.data.entities || [],
        enumerations: { VRBS_Country: countryRes.data.values || [] },
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load section");
    } finally {
      setLoading(false);
    }
  }, [period_id, section_n]);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      // Seed native lake fields on demand (idempotent).
      await api.post("/regulatory/lake/seed").catch(() => {});
      const { data } = await api.post(`/regulatory/omni/periods/${period_id}/refresh?section=${section_n}`);
      toast.success(`Refreshed ${data.refreshed} field(s) from ${data.mappings_evaluated} mapping(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      await api.post("/regulatory/lake/seed").catch(() => {});
      const { data } = await api.post("/regulatory/mappings/suggest", { section: Number(section_n) });
      // Auto-accept suggestions with confidence >= 0.9 (seed) and prompt for the rest
      let accepted = 0;
      for (const s of data.suggestions) {
        if (s.confidence >= 0.9) {
          await api.put("/regulatory/mappings", {
            field_ref: s.field_ref, lake_ref: s.lake_ref,
            confidence: s.confidence, proposed_by: s.proposed_by,
          });
          accepted++;
        }
      }
      toast.success(`${accepted}/${data.suggestions.length} mappings accepted (≥90% confidence)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suggest failed");
    } finally {
      setSuggesting(false);
    }
  };

  const stats = useMemo(() => {
    if (!payload) return { total: 0, mapped: 0, hasValue: 0 };
    return {
      total: payload.fields.length,
      mapped: Object.keys(payload.mappings || {}).length,
      hasValue: Object.keys(payload.values || {}).length,
    };
  }, [payload]);

  if (loading) return <FullPageMsg>Loading Section {section_n}…</FullPageMsg>;
  if (!payload) return <FullPageMsg>Section unavailable.</FullPageMsg>;

  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }} data-testid={`omni-section-${section_n}`}>
      <Hero
        section_n={section_n}
        title={payload.title}
        stats={stats}
        onRefresh={refresh}
        onSuggest={suggest}
        refreshing={refreshing}
        suggesting={suggesting}
      />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        <QuestionGroups payload={payload} onChange={load} />
      </div>
    </div>
  );
}

/* ─── Hero ─────────────────────────────────────────── */

function Hero({ section_n, title, stats, onRefresh, onSuggest, refreshing, suggesting }) {
  const pct = stats.total ? Math.round((stats.mapped / stats.total) * 100) : 0;
  return (
    <header
      className="relative overflow-hidden text-white"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #061633 65%, #020814 100%)` }}
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{ background: `${GOLD}18` }} />
      <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <Link
          to="/regulatory/dashboard"
          data-testid="link-back-to-dashboard"
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-white/70 hover:text-white"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Reporting Dashboard
        </Link>
        <div className="mt-4 flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-3">
            <span
              className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-mono font-semibold shrink-0"
              style={{ background: `${GOLD}22`, color: GOLD }}
            >
              {section_n}
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
                OMNI-Risk Return · Section {section_n}
              </div>
              <h1 className="font-display text-2xl lg:text-4xl font-semibold mt-1">{title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={onSuggest}
              disabled={suggesting}
              data-testid="btn-suggest-mappings"
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-semibold bg-white/10 hover:bg-white/20 disabled:opacity-50 transition"
            >
              <Sparkles className={`w-3.5 h-3.5 ${suggesting ? "animate-spin" : ""}`} />
              Suggest mappings
            </button>
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              data-testid="btn-refresh-section"
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-[12px] font-semibold text-white disabled:opacity-50"
              style={{ background: GOLD }}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh from data lake
            </button>
          </div>
        </div>
        <div className="mt-6 grid grid-cols-3 gap-4 max-w-xl">
          <MetricPill label="Fields"       value={stats.total} />
          <MetricPill label="Mapped"        value={`${stats.mapped}/${stats.total}`} />
          <MetricPill label="With value"    value={`${stats.hasValue}/${stats.total}`} />
        </div>
        <div className="mt-5 max-w-xl">
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: GOLD }} />
          </div>
        </div>
      </div>
    </header>
  );
}

function MetricPill({ label, value }) {
  return (
    <div className="rounded-lg border border-white/15 bg-white/5 px-3 py-2">
      <div className="text-[9px] uppercase tracking-widest text-white/50 font-semibold">{label}</div>
      <div className="text-lg font-display font-semibold text-white tabular-nums">{value}</div>
    </div>
  );
}

/* ─── Question grouping ─────────────────────────────── */

function QuestionGroups({ payload, onChange }) {
  // Group fields by qid (e.g. 9.1, 9.2, ...)
  const groups = useMemo(() => {
    const m = new Map();
    for (const f of payload.fields) {
      if (!m.has(f.qid)) m.set(f.qid, {
        qid: f.qid,
        title: f.question_title || (f.prompt || "").split("\n")[0],
        description: f.prompt_description || null,
        fields: [],
      });
      m.get(f.qid).fields.push(f);
    }
    return [...m.values()];
  }, [payload.fields]);

  return (
    <>
      {groups.map((g) => (
        <section key={g.qid} className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 lg:p-6" data-testid={`qgroup-${g.qid}`}>
          <header className="mb-4">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-[11px] font-semibold text-slate-400 shrink-0">Q{g.qid}</span>
              <h3 className="font-display text-[15px] font-semibold leading-snug" style={{ color: NAVY }}>
                {g.title || `Question ${g.qid}`}
              </h3>
            </div>
            {g.description && (
              <p className="mt-1.5 ml-6 text-[12.5px] text-slate-500 leading-relaxed" data-testid={`qgroup-${g.qid}-desc`}>
                {g.description}
              </p>
            )}
          </header>
          <div className="space-y-2">
            {g.fields.map((f) => (
              <FieldCard
                key={f.field_ref}
                field={f}
                mapping={payload.mappings[f.field_ref]}
                value={payload.values[f.field_ref]}
                periodId={payload.period_id}
                entities={payload.entities || []}
                enumerations={payload.enumerations || {}}
                onChange={onChange}
              />
            ))}
          </div>
        </section>
      ))}
    </>
  );
}

/* ─── Field card ──────────────────────────────────── */

/**
 * Auto-derived value for fields fed by other in-app data. Currently only
 * `OMNI-1.1._cond.iii` ("List of all entities forming part of the group")
 * which we auto-populate from the tenant's TenantEntity register.
 */
function deriveAutoValue(field, { entities }) {
  if (field.field_ref === "OMNI-1.1._cond.iii" && entities?.length) {
    return {
      value: entities
        .filter((e) => e.entity_type !== "parent")
        .map((e) => e.legal_name)
        .join(", "),
      source_hint: "Auto-populated from Tenant Entities register",
    };
  }
  return null;
}

function FieldCard({ field, mapping, value, periodId, entities, enumerations, onChange }) {
  const [editing, setEditing] = useState(false);
  const auto = useMemo(() => deriveAutoValue(field, { entities }), [field, entities]);
  const effectiveValue = value?.value ?? auto?.value ?? null;
  const sourceKind = value?.source_kind || (auto ? "native" : (mapping ? "connector" : "unsourced"));
  const chip = SOURCE_CHIP[sourceKind] || SOURCE_CHIP.unsourced;
  const statusKey = value?.status || (auto ? "ready" : "not_started");
  const statusUi = STATUS_ICON[statusKey] || STATUS_ICON.not_started;
  const StatusIcon = statusUi.Icon;
  const isConditional = Boolean(field.conditional_gate);

  return (
    <div
      className={`rounded-xl border p-4 hover:border-slate-300 transition ${isConditional ? "border-amber-200 bg-amber-50/40" : "border-slate-200"}`}
      data-testid={`field-${field.field_ref}`}
    >
      {isConditional && (
        <div className="mb-2 flex items-start gap-2 rounded-md bg-amber-100/60 border border-amber-200 px-2.5 py-1.5 text-[11px] text-amber-900">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span className="leading-snug">
            <span className="uppercase tracking-widest text-[9px] font-semibold mr-1">Conditional</span>
            {field.conditional_gate}
          </span>
        </div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] font-semibold text-slate-400">{field.field_ref}</span>
            <span
              className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
              style={{ background: "#F1F5F9", color: "#475569" }}
            >
              {field.field_type}
            </span>
            {field.required && !isConditional && (
              <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded"
                style={{ background: "#FEE2E2", color: "#991B1B" }}>required</span>
            )}
            {isConditional && (
              <span className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded"
                style={{ background: "#FEF3C7", color: "#92400E" }}>if gate met</span>
            )}
          </div>
          <div className="mt-1 text-[13.5px] text-slate-800 font-medium">
            <span className="text-slate-400 mr-1">({field.option_key || "—"})</span>
            {field.label}
          </div>
        </div>
        <div className="text-right shrink-0 max-w-xs">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Value</div>
          <div className="text-lg font-display font-semibold tabular-nums leading-snug break-words" style={{ color: NAVY }} data-testid={`value-${field.field_ref}`}>
            {formatValue(effectiveValue, field.field_type, enumerations)}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap text-[10.5px]">
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold uppercase tracking-widest text-[9px]"
          style={{ background: chip.bg, color: chip.fg }}>
          <Database className="w-2.5 h-2.5" />
          {chip.label}
        </span>
        <StatusIcon
          className={`w-3.5 h-3.5 ${statusKey === "in_progress" ? "animate-spin" : ""}`}
          style={{ color: statusUi.color }}
        />
        {mapping && (
          <span className="inline-flex items-center gap-1 text-slate-500">
            <Link2 className="w-3 h-3" />
            <code className="text-[10.5px] font-mono">{mapping.lake_ref}</code>
          </span>
        )}
        {auto && !value && (
          <span className="text-slate-400">· {auto.source_hint}</span>
        )}
        {value?.source_system && (
          <span className="text-slate-400">· {value.source_system}</span>
        )}
        {value?.computed_at && (
          <span className="inline-flex items-center gap-1 text-slate-400">
            <Clock className="w-2.5 h-2.5" />
            {formatDistanceToNow(new Date(value.computed_at), { addSuffix: true })}
          </span>
        )}
        {value?.human_overridden && (
          <span className="inline-flex items-center gap-1 text-amber-700">
            <User className="w-3 h-3" />
            override by {value.override_by}
          </span>
        )}
        <button
          type="button"
          onClick={() => setEditing((e) => !e)}
          data-testid={`btn-override-${field.field_ref}`}
          className="ml-auto inline-flex items-center gap-1 text-[10.5px] font-semibold text-slate-500 hover:text-slate-900"
        >
          {editing ? <><X className="w-3 h-3" /> Cancel</> : <><Pencil className="w-3 h-3" /> Override</>}
        </button>
      </div>

      {editing && (
        <OverrideForm
          field={field}
          currentValue={effectiveValue}
          periodId={periodId}
          enumerations={enumerations}
          onDone={() => { setEditing(false); onChange(); }}
        />
      )}
    </div>
  );
}

function OverrideForm({ field, currentValue, periodId, enumerations, onDone }) {
  const [val, setVal] = useState(currentValue ?? "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!reason.trim()) return toast.error("Reason is required for overrides");
    setSaving(true);
    try {
      const casted = coerce(val, field.field_type);
      await api.put(`/regulatory/omni/periods/${periodId}/values/${encodeURIComponent(field.field_ref)}`, {
        value: casted, reason,
      });
      toast.success(`Override saved · ${field.field_ref}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const inputControl = (() => {
    if (field.field_type === "enum:country") {
      const countries = enumerations?.VRBS_Country || [];
      return (
        <select
          data-testid={`override-value-${field.field_ref}`}
          value={val ?? ""}
          onChange={(e) => setVal(e.target.value)}
          className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[13px] focus:outline-none focus:border-slate-500"
        >
          <option value="">— Select a country —</option>
          {countries.map((c) => (
            <option key={c.alpha3 || c.key} value={c.alpha3 || c.key}>
              {c.alpha3 ? `${c.alpha3} · ${c.label}` : c.label}
            </option>
          ))}
        </select>
      );
    }
    if (field.field_type === "yesno" || field.field_type === "boolean") {
      return (
        <select
          data-testid={`override-value-${field.field_ref}`}
          value={val === true || val === "true" || val === "Yes" ? "Yes" : val === false || val === "false" || val === "No" ? "No" : ""}
          onChange={(e) => setVal(e.target.value)}
          className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[13px] focus:outline-none focus:border-slate-500"
        >
          <option value="">— Select —</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      );
    }
    return (
      <input
        data-testid={`override-value-${field.field_ref}`}
        value={val ?? ""}
        onChange={(e) => setVal(e.target.value)}
        type={["number", "amount", "percentage"].includes(field.field_type) ? "number" : "text"}
        className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-[13px] font-mono focus:outline-none focus:border-slate-500"
      />
    );
  })();

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-3">
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Value</span>
        {inputControl}
      </label>
      <label className="block">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Reason (required)</span>
        <input
          data-testid={`override-reason-${field.field_ref}`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is this being overridden?"
          className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-[13px] focus:outline-none focus:border-slate-500"
        />
      </label>
      <button
        type="button"
        onClick={save}
        disabled={saving}
        data-testid={`override-save-${field.field_ref}`}
        className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-[12px] font-semibold text-white disabled:opacity-50"
        style={{ background: NAVY }}
      >
        <Save className="w-3 h-3" />
        Save override
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}

/* ─── helpers ─────────────────────────────────────── */

function formatValue(v, type, enumerations) {
  if (v === null || v === undefined || v === "") return "—";
  if (type === "amount") return `R ${Number(v).toLocaleString()}`;
  if (type === "number" || type === "percentage") return Number(v).toLocaleString();
  if (type === "boolean" || type === "yesno") return v === true || v === "Yes" || v === "true" ? "Yes" : "No";
  if (type === "enum:country") {
    const countries = enumerations?.VRBS_Country || [];
    const match = countries.find((c) => c.alpha3 === v || c.alpha2 === v || c.label === v || c.key === v);
    return match ? (match.alpha3 ? `${match.alpha3} · ${match.label}` : match.label) : String(v);
  }
  return String(v);
}
function coerce(raw, type) {
  if (raw === "" || raw === null || raw === undefined) return null;
  if (type === "number" || type === "amount" || type === "percentage") return Number(raw);
  if (type === "boolean" || type === "yesno") return String(raw).toLowerCase() === "yes" || raw === true;
  return raw;
}

function FullPageMsg({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "#F8F9FA" }}>
      <div className="rounded-xl bg-white border border-slate-200 p-6 text-slate-600 text-[13px] flex items-center gap-2">
        <FileCheck2 className="w-4 h-4" />
        {children}
      </div>
    </div>
  );
}
